import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Eye, Loader2 } from 'lucide-react';
import SimpleSelect, { type SelectOption } from '@/components/ui/SimpleSelect';
import Modal from '@/components/ui/Modal';
import certificateTemplateService from '@/services/certificate-template.service';
import { CertificateTemplateDto, CertificateTemplateType } from '@/types/certificate-template.types';

interface CertificateTemplateSelectProps {
  templateType: CertificateTemplateType;
  value: string;                       // '' = follow the default template
  onChange: (templateId: string) => void;
  disabled?: boolean;
}

/** Same clean-up as the templates list: keep the {{#if}} content so the preview isn't empty */
const previewHtml = (html: string) =>
  html.replace(/\{\{#if\s+\w+\}\}([\s\S]*?)(?:\{\{else\}\}[\s\S]*?)?\{\{\/if\}\}/g, '$1');

/** Full certificate (842 x 595) scaled down to the width it gets in the preview popup */
function CertificatePreview({ template }: { template: CertificateTemplateDto }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.8);
  useEffect(() => {
    if (!box.current) return;
    const observer = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / 842));
    observer.observe(box.current);
    return () => observer.disconnect();
  }, []);
  return (
    // Width capped so the whole page fits the popup's height (80vh minus header and padding)
    <div ref={box} className="w-full mx-auto border border-gray-200 rounded overflow-hidden bg-white" style={{ aspectRatio: '842 / 595', maxWidth: 'calc((80vh - 200px) * 842 / 595)' }}>
      <iframe
        srcDoc={previewHtml(template.html_template || '')}
        title={`Preview: ${template.template_name}`}
        className="border-0 pointer-events-none bg-white"
        style={{ width: 842, height: 595, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        sandbox="allow-same-origin"
      />
    </div>
  );
}

/**
 * Which certificate template a course / learning path / certification tier issues with.
 * The "(Default)" option saves '' so it keeps following the default if it is switched later;
 * any other option pins that template.
 */
export default function CertificateTemplateSelect({ templateType, value, onChange, disabled }: CertificateTemplateSelectProps) {
  const { data: templates, isLoading, isError } = useQuery({
    queryKey: ['certificateTemplates', 'of-type', templateType],
    queryFn: () => certificateTemplateService.getAllTemplatesOfType(templateType)
  });
  // Same lookup the backend uses when nothing is chosen
  const { data: inUse, isLoading: inUseLoading, isError: inUseFailed } = useQuery({
    queryKey: ['certificateTemplates', 'in-use', templateType],
    queryFn: () => certificateTemplateService.getTemplateByType(templateType)
  });
  const [previewOpen, setPreviewOpen] = useState(false);

  // Chosen earlier but deleted since: switch the form to the default (a deleted id can't be saved)
  const chosenMissing = !!value && !!templates && !templates.some(t => t._id === value);
  const [clearedMissing, setClearedMissing] = useState(false);
  // Parents pass an inline onChange; a ref keeps this effect from re-running on every render
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (!chosenMissing || disabled) return; // read-only (published): leave the form untouched
    setClearedMissing(true);
    onChangeRef.current('');
  }, [chosenMissing, disabled]);

  if (isLoading || inUseLoading) {
    return (
      <p className="text-xs text-gray-400 flex items-center gap-1 py-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading templates…
      </p>
    );
  }
  if (isError) {
    return <p className="text-xs text-red-500 py-2">Could not load certificate templates. Reopen this dialog to try again.</p>;
  }

  // The default template is listed once, first, as the "follow the default" option
  const options: SelectOption[] = [
    { value: '', label: inUse ? `${inUse.template_name} (Default)` : inUseFailed ? 'Default template (could not load its name)' : 'Default template (none set yet)' },
    ...(templates || []).filter(t => t._id !== inUse?._id).map(t => ({ value: t._id, label: t.template_name }))
  ];
  // A pin to the current default looks the same as following it; untouched, it saves unchanged
  const shown = value && value !== inUse?._id ? value : '';
  const previewTemplate: CertificateTemplateDto | null | undefined =
    shown ? templates?.find(t => t._id === shown) : inUse;

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0" data-template-select={templateType}>
          <SimpleSelect
            value={shown}
            options={options}
            onChange={v => { setClearedMissing(false); onChange(v ? String(v) : ''); }}
            isDisabled={disabled}
            isSearchable={options.length > 6}
            aria-label="Certificate template"
            formatOptionLabel={(o: SelectOption) => {
              const isDefault = o.value === '';
              const name = isDefault && inUse ? inUse.template_name : o.label;
              return (
                <span className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{name}</span>
                  {isDefault && inUse && (
                    <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                      Default
                    </span>
                  )}
                </span>
              );
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          disabled={!previewTemplate}
          className="h-[42px] px-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Eye className="w-4 h-4" /> Preview
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-1.5">
        {shown
          ? 'This template is always used, even if the default changes.'
          : value && value === inUse?._id
            // Chosen by name earlier, and it has since become the default: it won't follow a later switch
            ? 'This template was chosen by name (it is also the default now), so it stays even if the default changes. Pick the Default option again to follow future changes.'
            : 'Uses the default template and follows it if the default changes.'}
      </p>
      {clearedMissing && !value && (
        <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
          <AlertTriangle className="w-3 h-3" />
          The template chosen earlier was deleted, so the default is used. Save to confirm.
        </p>
      )}

      {previewTemplate && (
        <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title={previewTemplate.template_name} subtitle="Certificate preview" size="xl">
          <CertificatePreview template={previewTemplate} />
        </Modal>
      )}
    </div>
  );
}
